import { useMemo, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { ErrorBanner } from "@/shared/ui";
import { CampoCerca, ChipFiltro } from "@/shared/ui/ChipFiltro";
import { type AzioneLane, LaneEffettiConfirmDialog } from "./LaneEffettiConfirmDialog";
import { type SyncInterval, runIntervalNow, updateInterval } from "./api";
import {
  AREE,
  LANE,
  type Lettura,
  STATI_PROBLEMA,
  type StatoLane,
  areaLane,
  nomeLane,
  ogniQuanto,
  quandoBreve,
} from "./automatismi";
import { Foglio, Interruttore, Pillola, TASTO, type Tono } from "./parti";

// Gli automatismi (le lane dello scheduler): uno per riga, divisi per area,
// col loro ultimo giro. Ridisegno 28/10/2026: prima era un elenco piatto di
// slug con «Ultimo enqueue», che diceva quando la lane era stata ACCODATA,
// non se aveva funzionato.

/** Scelte della tendina degli intervalli. Non sono l'elenco completo: in DB
 *  vivono lane a 86400 e 21600. Se il valore vero non avesse la sua <option>
 *  il campo apparirebbe VUOTO e il primo salvataggio ci scriverebbe sopra un
 *  intervallo che nessuno ha scelto: per questo il corrente si aggiunge
 *  sempre, al suo posto. */
const SCELTE_INTERVALLO = [60, 120, 180, 300, 600, 900, 1800, 3600, 7200];

export const STATO_LANE: Record<StatoLane, { tono: Tono; nome: string }> = {
  fermata: { tono: "errore", nome: "Fermato" },
  errore: { tono: "errore", nome: "Errore" },
  ritardo: { tono: "attenzione", nome: "In ritardo" },
  in_corso: { tono: "info", nome: "In corso" },
  ok: { tono: "ok", nome: "Funziona" },
  spenta: { tono: "spento", nome: "Spento" },
  mai: { tono: "spento", nome: "Mai partito" },
};

type Filtro = "tutti" | "problemi" | "accesi" | "spenti";

function descriviQuando(l: Lettura): string {
  const q = quandoBreve(l.quando);
  switch (l.stato) {
    case "fermata":
      return q ? `fermato ${q}, riparte al riavvio` : "riparte al riavvio";
    case "errore":
      return q ? `ultimo giro ${q}` : "";
    case "ritardo":
      return q ? `ultimo giro riuscito ${q}` : "";
    case "in_corso":
      return q ? `partito ${q}` : "sta girando";
    case "ok":
      return q ? `ultimo giro ${q}` : "";
    case "spenta":
      return q ? `ultimo giro ${q}` : "mai girato";
    default:
      return "non ha ancora girato";
  }
}

export function IntervalsList({
  intervals,
  letture,
  loading,
  loadError = null,
  onRefresh,
}: {
  intervals: SyncInterval[];
  /** Stato di ogni lane (useAutomatismi). Facoltativo: senza, le righe
   *  mostrano solo acceso/spento. */
  letture?: Map<string, Lettura>;
  loading: boolean;
  /** Errore della GET: senza, una lista vuota si leggeva come «nessuna
   *  sincronizzazione configurata». */
  loadError?: string | null;
  onRefresh: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<Filtro>("tutti");
  const [cerca, setCerca] = useState("");
  // Conferma in sospeso per le lane che agiscono fuori. Solo per ACCENDERE
  // ed ESEGUIRE: spegnere non si conferma, chiedere una conferma per fermare
  // qualcosa che sta andando storto e' esattamente il momento sbagliato.
  const [daConfermare, setDaConfermare] = useState<{
    module: string;
    azione: AzioneLane;
  } | null>(null);

  const lettura = (i: SyncInterval): Lettura =>
    letture?.get(i.module) ?? {
      stato: i.enabled ? "ok" : "spenta",
      quando: i.last_enqueued_at,
      motivo: null,
    };

  const conteggi = useMemo(() => {
    let problemi = 0;
    let accesi = 0;
    for (const i of intervals) {
      if (i.enabled) accesi += 1;
      const l = letture?.get(i.module);
      if (l && STATI_PROBLEMA.has(l.stato)) problemi += 1;
    }
    return { tutti: intervals.length, problemi, accesi, spenti: intervals.length - accesi };
  }, [intervals, letture]);

  const visibili = useMemo(() => {
    const q = cerca.trim().toLowerCase();
    return intervals.filter((i) => {
      const l = letture?.get(i.module);
      if (filtro === "problemi" && !(l && STATI_PROBLEMA.has(l.stato))) return false;
      if (filtro === "accesi" && !i.enabled) return false;
      if (filtro === "spenti" && i.enabled) return false;
      if (!q) return true;
      const info = LANE[i.module];
      return [i.module, info?.nome, info?.cosa].some((t) => t?.toLowerCase().includes(q));
    });
  }, [intervals, letture, filtro, cerca]);

  async function esegui(module: string, op: () => Promise<unknown>) {
    setBusy(module);
    try {
      await op();
      setError(null);
      onRefresh();
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setBusy(null);
    }
  }

  const toggle = (module: string, enabled: boolean) =>
    void esegui(module, () => updateInterval(module, { enabled }));
  const cambiaIntervallo = (module: string, seconds: number) =>
    void esegui(module, () => updateInterval(module, { interval_seconds: seconds }));
  const subito = (module: string) => void esegui(module, () => runIntervalNow(module));

  /** Interpone la conferma quando la lane agisce fuori; altrimenti esegue. */
  function chiedi(module: string, azione: AzioneLane) {
    if (LANE[module]?.effetto) {
      setDaConfermare({ module, azione });
      return;
    }
    if (azione === "esegui") subito(module);
    else toggle(module, true);
  }

  function confermato() {
    if (!daConfermare) return;
    const { module, azione } = daConfermare;
    setDaConfermare(null);
    if (azione === "esegui") subito(module);
    else toggle(module, true);
  }

  if (loading && intervals.length === 0 && !loadError) {
    return (
      <Foglio className="px-5 py-6">
        <p className="text-[13px] text-ink-2">Carico gli automatismi…</p>
      </Foglio>
    );
  }

  return (
    <>
      {daConfermare && (
        <LaneEffettiConfirmDialog
          nome={nomeLane(daConfermare.module)}
          effetto={LANE[daConfermare.module]?.effetto ?? ""}
          azione={daConfermare.azione}
          onConfirm={confermato}
          onCancel={() => setDaConfermare(null)}
        />
      )}
      <ErrorBanner message={loadError ?? error} className="mb-3" />
      {loadError && error && <ErrorBanner message={error} className="mb-3" />}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 max-md:-mx-3 max-md:w-[calc(100%+1.5rem)] max-md:overflow-x-auto max-md:px-3 max-md:pb-1 max-md:[scrollbar-width:none]">
          <ChipFiltro
            tono="annullato"
            attivo={filtro === "tutti"}
            onClick={() => setFiltro("tutti")}
            conteggio={conteggi.tutti}
          >
            Tutti
          </ChipFiltro>
          <ChipFiltro
            tono="non-ordinato"
            attivo={filtro === "problemi"}
            onClick={() => setFiltro("problemi")}
            conteggio={conteggi.problemi}
            evidenzia={conteggi.problemi > 0}
          >
            Da guardare
          </ChipFiltro>
          <ChipFiltro
            tono="pronto"
            attivo={filtro === "accesi"}
            onClick={() => setFiltro("accesi")}
            conteggio={conteggi.accesi}
          >
            Accesi
          </ChipFiltro>
          <ChipFiltro
            tono="annullato"
            attivo={filtro === "spenti"}
            onClick={() => setFiltro("spenti")}
            conteggio={conteggi.spenti}
          >
            Spenti
          </ChipFiltro>
        </div>
        <CampoCerca
          value={cerca}
          onChange={setCerca}
          placeholder="Cerca un automatismo"
          ariaLabel="Cerca un automatismo"
          className="ml-auto w-[260px] max-md:order-first max-md:ml-0 max-md:w-full"
        />
      </div>

      {visibili.length === 0 && (
        <Foglio className="px-5 py-6">
          <p className="text-[13px] text-ink max-md:text-[15px]">
            {filtro === "problemi" && !cerca
              ? "Niente da guardare: ogni automatismo acceso ha girato bene."
              : "Nessun automatismo corrisponde."}
          </p>
        </Foglio>
      )}

      <div className="space-y-5">
        {AREE.map((area) => {
          const righe = visibili
            .filter((i) => areaLane(i.module) === area.id)
            .sort((a, b) => nomeLane(a.module).localeCompare(nomeLane(b.module), "it"));
          if (righe.length === 0) return null;
          return (
            <div key={area.id}>
              <h3 className="mb-2 px-1 text-[13px] font-bold text-ink max-md:text-[15px]">
                {area.nome}
                <span className="ml-2 font-semibold tabular-nums text-ink-3">{righe.length}</span>
              </h3>
              <Foglio>
                <ul>
                  {righe.map((i) => (
                    <RigaLane
                      key={i.module}
                      interval={i}
                      lettura={lettura(i)}
                      busy={busy === i.module}
                      onToggle={() =>
                        i.enabled ? toggle(i.module, false) : chiedi(i.module, "accendi")
                      }
                      onEsegui={() => chiedi(i.module, "esegui")}
                      onIntervallo={(s) => cambiaIntervallo(i.module, s)}
                    />
                  ))}
                </ul>
              </Foglio>
            </div>
          );
        })}
      </div>
    </>
  );
}

function RigaLane({
  interval,
  lettura,
  busy,
  onToggle,
  onEsegui,
  onIntervallo,
}: {
  interval: SyncInterval;
  lettura: Lettura;
  busy: boolean;
  onToggle: () => void;
  onEsegui: () => void;
  onIntervallo: (seconds: number) => void;
}) {
  const info = LANE[interval.module];
  const nome = nomeLane(interval.module);
  const stato = STATO_LANE[lettura.stato];
  const idEffetto = `lane-effetto-${interval.module}`;
  const motivo = lettura.motivo ? prettifyApiError(lettura.motivo) : null;

  return (
    <li
      id={`lane-${interval.module}`}
      className="grid scroll-mt-24 grid-cols-[minmax(0,1fr)_190px_auto_auto] items-start gap-x-5 gap-y-2 border-t border-line px-5 py-3.5 first:border-t-0 max-md:grid-cols-[minmax(0,1fr)_auto] max-md:gap-x-3 max-md:px-4 max-md:py-4"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-[14px] font-bold text-ink max-md:text-[16px]">{nome}</span>
          {info?.effetto && (
            <span className="rounded-full border border-amber px-2 py-px text-[11px] font-bold text-ink max-md:text-[12px]">
              Agisce fuori
            </span>
          )}
        </div>
        {info?.cosa && (
          <p className="mt-0.5 text-[12.5px] leading-snug text-ink-2 max-md:text-[14.5px] max-md:text-ink">
            {info.cosa}
          </p>
        )}
        {info?.effetto && (
          <details className="group mt-1">
            <summary className="inline-flex cursor-pointer list-none items-center gap-1 rounded text-[12px] font-semibold text-ink underline decoration-line-2 underline-offset-2 hover:decoration-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-4 max-md:py-1 max-md:text-[14px] [&::-webkit-details-marker]:hidden">
              Cosa succede fuori
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
                className="h-3 w-3 fill-none stroke-current stroke-2 transition-transform group-open:rotate-180"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </summary>
            <p
              id={idEffetto}
              className="mt-1.5 max-w-[68ch] rounded-md bg-amber-soft px-3 py-2 text-[12.5px] leading-relaxed text-ink max-md:text-[14px]"
            >
              {info.effetto}
            </p>
          </details>
        )}
        {motivo && (lettura.stato === "errore" || lettura.stato === "fermata") && (
          <p className="mt-1.5 max-w-[68ch] break-words rounded-md bg-red-soft px-3 py-2 text-[12.5px] leading-snug text-ink max-md:text-[14px]">
            {motivo}
          </p>
        )}
      </div>

      {/* Stato: sul telefono va nella seconda riga, sotto il nome. */}
      <div className="flex flex-col items-start gap-1 max-md:col-span-2 max-md:row-start-2 max-md:flex-row max-md:flex-wrap max-md:items-center max-md:gap-2">
        <Pillola tono={stato.tono}>{stato.nome}</Pillola>
        <span className="text-[12px] tabular-nums text-ink-2 max-md:text-[13.5px] max-md:text-ink">
          {descriviQuando(lettura)}
        </span>
      </div>

      {/* Controlli una volta sola (niente doppioni per il telefono): la
          griglia li sposta. Computer: tutto sulla stessa riga. Telefono:
          l'interruttore in alto a destra, intervallo ed «Esegui ora» in
          fondo alla scheda, tasti da pollice. */}
      <div className="flex items-center gap-2 max-md:col-span-2 max-md:row-start-3">
        <label className="max-md:min-w-0 max-md:flex-1">
          <span className="sr-only">Ogni quanto gira {nome}</span>
          <SceltaIntervallo
            value={interval.interval_seconds}
            disabled={busy}
            onChange={onIntervallo}
          />
        </label>
        <button
          type="button"
          disabled={busy}
          aria-describedby={info?.effetto ? idEffetto : undefined}
          onClick={onEsegui}
          className={TASTO}
        >
          {busy ? "Avvio…" : "Esegui ora"}
        </button>
      </div>
      <div className="pt-1 max-md:col-start-2 max-md:row-start-1 max-md:pt-0">
        <Interruttore
          acceso={interval.enabled}
          label={`Automatismo ${nome}`}
          describedBy={info?.effetto ? idEffetto : undefined}
          disabled={busy}
          onClick={onToggle}
        />
      </div>
    </li>
  );
}

function SceltaIntervallo({
  value,
  disabled,
  onChange,
}: {
  value: number;
  disabled: boolean;
  onChange: (seconds: number) => void;
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(Number(e.target.value))}
      className="h-8 rounded-full border border-line-2 bg-card pl-3 pr-7 text-[12.5px] font-semibold tabular-nums text-ink hover:border-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-4 disabled:opacity-50 max-md:h-11 max-md:w-full max-md:text-[14px]"
    >
      {[...new Set([...SCELTE_INTERVALLO, value])]
        .sort((a, b) => a - b)
        .map((s) => (
          <option key={s} value={s}>
            {ogniQuanto(s)}
          </option>
        ))}
    </select>
  );
}
